import type { BulkValidateReviewEntry } from '../hooks/useBulkValidateReviewEntries'
import type { ReviewEntryValidationState } from '../hooks/useReviewEntriesValidation'
import type { ReviewEntryItem } from '../types/reviewEntryItem'
import { applyReviewEntryDraftToItem } from './applyReviewEntryDraftToItem'
import type { MatchableTaxRate } from './getMatchedVatRateId'
import { getReviewEntryContactId } from './getReviewEntryContactId'
import { getReviewEntrySourceFingerprint } from './getReviewEntrySourceFingerprint'
import { getReviewEntryValidationFlags, type ReviewEntryValidationFlags } from './getReviewEntryValidationFlags'
import { getReviewEntryDraft } from './reviewEntryDraftStorage'

interface GetBulkValidateReviewEntriesFromStatesOptions {
  organizationId?: string
  purchaseTaxRates: MatchableTaxRate[]
  shouldInclude: (flags: ReviewEntryValidationFlags) => boolean
  states: ReviewEntryValidationState[]
}

const getFlushedSupplierId = (state: ReviewEntryValidationState, organizationId: string | undefined): string | null => {
  if (!organizationId) {
    return state.supplierId
  }

  const draft = getReviewEntryDraft({
    entryId: state.entryId,
    organizationId,
    sourceFingerprint: getReviewEntrySourceFingerprint(state.item),
  })

  if (draft === undefined) {
    return state.supplierId
  }

  if (draft.supplier === null) {
    return null
  }

  const draftSupplierId = draft.supplier?.id?.trim()

  if (!draftSupplierId) {
    return state.supplierId
  }

  if (draftSupplierId === state.supplierId) {
    return state.supplierId
  }

  const apiContactId = getReviewEntryContactId(state.item.processingDecision)

  // a draft that still carries the API-matched contact has not passed the contact-fetch gate
  // (404 or in-flight). a different id was picked in the open form, so it is safe to use.
  if (draftSupplierId === apiContactId) {
    return null
  }

  return draftSupplierId
}

// mirrors the detail-form required line fields that `getReviewEntryValidationFlags` does not
// cover (it only gates supplier + VAT). a flushed draft can clear `accountId` while leaving
// VAT intact, which the open form would block on submit.
const hasRequiredFormFields = (item: ReviewEntryItem): boolean => {
  const entryLines = item.entryLines ?? []

  if (!entryLines.length) {
    return false
  }

  return entryLines.every((line) => {
    const accountId = line.accountId?.trim() ?? ''
    const amountExclVat = line.amountExclVat ?? line.amountExclTax
    const amountInclVat = line.amountInclVat ?? line.amountInclTax

    return accountId !== '' && amountExclVat != null && amountInclVat != null
  })
}

export const getBulkValidateReviewEntriesFromStates = ({
  organizationId,
  purchaseTaxRates,
  shouldInclude,
  states,
}: GetBulkValidateReviewEntriesFromStatesOptions): BulkValidateReviewEntry[] =>
  states.reduce<BulkValidateReviewEntry[]>((entries, state) => {
    const supplierId = getFlushedSupplierId(state, organizationId)

    if (supplierId === null) {
      return entries
    }

    // `documentId` is required to bulk-validate, so the draft key does not depend on list index
    const itemWithDraft = applyReviewEntryDraftToItem(state.item, 0, organizationId)
    const flags = getReviewEntryValidationFlags({
      item: itemWithDraft,
      supplierId,
      vatRates: purchaseTaxRates,
    })

    if (!shouldInclude(flags) || !hasRequiredFormFields(itemWithDraft)) {
      return entries
    }

    entries.push({ item: state.item, supplierId })

    return entries
  }, [])
